"use client";
import { useEffect, type ReactNode } from "react";
import { BlockProvider } from "./blocks/context";
import { BlocksView } from "./blocks/BlocksView";
import { getTheme } from "./themes";
import { ModuleHeaderActions } from "./modules/ModuleSiteComponents";
import { LangAutoRedirect } from "./LangSwitcher";
import type { BlockData } from "./types";

/** Renders the public website: theme header + blocks/content + theme footer. */
export function SiteShell(props: {
  theme?: string;
  localeDomains?: Record<string, string>;
  localeAlternates?: Record<string, string>;
  headerAttrs: Record<string, unknown>;
  footerAttrs: Record<string, unknown>;
  blocks?: BlockData[];
  blockData?: Record<string, unknown>;
  locale: string;
  locales: string[];
  enabledModules: string[];
  children?: ReactNode;
}) {
  const theme = getTheme(props.theme);
  useEffect(() => {
    document.documentElement.lang = props.locale;
    window.__CMS_I18N = { domains: props.localeDomains, alternates: props.localeAlternates };
  }, [props.locale, props.localeDomains, props.localeAlternates]);

  const chromeCtx = (attrs: Record<string, unknown>) => ({
    attrs,
    data: undefined,
    locale: props.locale,
    locales: props.locales,
    localeDomains: props.localeDomains,
    localeAlternates: props.localeAlternates,
    enabledModules: props.enabledModules,
    editing: false,
    blockIndex: -1,
    scope: [] as (string | number)[],
    setAttr: () => {},
  });

  const skipLabel = { nl: "Naar hoofdinhoud", en: "Skip to main content", de: "Zum Hauptinhalt", fr: "Aller au contenu principal" }[props.locale] || "Skip to main content";

  return (
    <div className={`cms-theme-scope cms-theme-${theme.id}`}>
      <LangAutoRedirect locale={props.locale} locales={props.locales} domains={props.localeDomains} alternates={props.localeAlternates} />
      <a href="#main" className="cms-skip-link">{skipLabel}</a>
      <div className="cms-site-header">
        <BlockProvider value={chromeCtx(props.headerAttrs)}>
          <theme.Header />
        </BlockProvider>
        <ModuleHeaderActions enabledModules={props.enabledModules} locale={props.locale} />
      </div>
      <main id="main" tabIndex={-1}>
        {props.blocks && (
          <BlocksView
            themeId={props.theme}
            blocks={props.blocks}
            blockData={props.blockData || {}}
            locale={props.locale}
            locales={props.locales}
            localeDomains={props.localeDomains}
            localeAlternates={props.localeAlternates}
            enabledModules={props.enabledModules}
          />
        )}
        {props.children}
      </main>
      <BlockProvider value={chromeCtx(props.footerAttrs)}>
        <theme.Footer />
      </BlockProvider>
    </div>
  );
}
